import React, { useState, useEffect, useContext } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Select from 'react-select';
import { components } from 'react-select';
import { AuthContext } from '../../../context/AuthContext';
import messageService from '../../../services/messageService';
import userService from '../../../services/userService';
import messageCategoryService from '../../../services/messageCategoryService';
import { FaPaperPlane } from 'react-icons/fa';

const MessageCompose = ({ show, onHide, replyTo, onMessageSent }) => {
    const { user } = useContext(AuthContext);
    const [recipients, setRecipients] = useState([]);
    const [ccRecipients, setCcRecipients] = useState([]);
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState(null);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [attachments, setAttachments] = useState([]);

    useEffect(() => {
        if (show) {
            if (replyTo) {
                setSubject(`Re: ${replyTo.subject.startsWith('Re: ') ? replyTo.subject.slice(4) : replyTo.subject}`);
                if (replyTo.senderId) {
                    setRecipients([{
                        value: replyTo.senderId,
                        label: replyTo.senderName || 'Unknown User'
                    }]);
                }
                if (replyTo.content) {
                    setContent(`<br><br><hr><blockquote>${replyTo.content}</blockquote>`);
                }
                if (replyTo.categoryId) {
                    setCategory({ value: replyTo.categoryId, label: replyTo.categoryName });
                }
            }
            loadUsers();
            loadCategories();
        }
    }, [show, replyTo]);

    const loadUsers = async () => {
        try {
            const data = await userService.getAllUsers();
            const formattedUsers = data.map(user => {
                let displayName = user.username; // Default to username
                
                if (user.firstName && user.lastName) {
                    displayName = `${user.firstName} ${user.lastName}`;
                } else if (user.firstName) {
                    displayName = user.firstName;
                } else if (user.lastName) {
                    displayName = user.lastName;
                }

                return {
                    value: user.id,
                    label: displayName,
                    email: user.email || 'No email',
                    customLabel: `${displayName} (${user.email || 'No email'})`,
                    username: user.username,
                    personnelCode: user.personnelCode
                };
            });
            setUsers(formattedUsers);
        } catch (error) {
            console.error('Error loading users:', error);
            setError('Failed to load users');
        }
    };

    const loadCategories = async () => {
        try {
            console.log('Loading categories...');
            const data = await messageCategoryService.getAll();
            console.log('Categories data:', data);
            if (data && Array.isArray(data)) {
                const formattedCategories = data.map(category => ({
                    value: category.id,
                    label: category.name,
                    description: category.description,
                    defaultRecipientIds: category.defaultRecipientIds
                }));
                console.log('Formatted categories:', formattedCategories);
                setCategories(formattedCategories);
            } else {
                console.error('Invalid categories data:', data);
                setError('Invalid categories data received');
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            setError('Failed to load message categories');
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prevAttachments => [...prevAttachments, ...files]);
    };

    const removeAttachment = (index) => {
        setAttachments(prevAttachments => prevAttachments.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // Validate that either category or recipients are selected
            if (!category && (!recipients || recipients.length === 0)) {
                setError('Please select either a message category or recipients');
                setLoading(false);
                return;
            }

            const messageData = {
                subject,
                content,
                senderId: user?.id,
                categoryId: category?.value || null,
                recipientIds: recipients?.map(r => r.value) || [],
                ccRecipientIds: ccRecipients?.map(r => r.value) || [],
                parentMessageId: replyTo?.id
            };

            const response = await messageService.create(messageData, attachments);
            onMessageSent(response);
            onHide();
            
            // Reset form
            setSubject('');
            setContent('');
            setRecipients([]);
            setCcRecipients([]);
            setCategory(null);
            setAttachments([]);
        } catch (err) {
            console.error('Error sending message:', err);
            setError(err.response?.data?.message || 'Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return null;
    }

    // Custom option component for user selection
    const customOption = ({ data, ...props }) => (
        <components.Option {...props}>
            <div>
                <div>{data.customLabel}</div>
            </div>
        </components.Option>
    );

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{replyTo ? 'Reply to Message' : 'Compose New Message'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Message Category</Form.Label>
                        <Form.Select
                            value={category?.value || ''}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                const selectedCategory = categories.find(c => c.value === parseInt(selectedId));
                                setCategory(selectedCategory || null);
                            }}
                        >
                            <option value="">Select a category...</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>To</Form.Label>
                        <Select
                            isMulti
                            value={recipients}
                            onChange={setRecipients}
                            options={users}
                            placeholder="Select recipients..."
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isSearchable
                            noOptionsMessage={() => "No users found"}
                            components={{ Option: customOption }}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>CC</Form.Label>
                        <Select
                            isMulti
                            value={ccRecipients}
                            onChange={setCcRecipients}
                            options={users}
                            placeholder="Select CC recipients..."
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isSearchable
                            noOptionsMessage={() => "No users found"}
                            components={{ Option: customOption }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Message</Form.Label>
                        <ReactQuill
                            value={content}
                            onChange={setContent}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                    ['link', 'image'],
                                    ['clean']
                                ]
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Attachments</Form.Label>
                        <Form.Control
                            type="file"
                            multiple
                            onChange={handleFileChange}
                        />
                        {attachments.length > 0 && (
                            <div className="mt-2">
                                {attachments.map((file, index) => (
                                    <div key={index} className="d-flex align-items-center mb-1">
                                        <span className="me-2">{file.name}</span>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeAttachment(index)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onHide}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading} className="d-inline-flex align-items-center gap-2">
                            <FaPaperPlane /> {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default MessageCompose;